import React, { useEffect, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Theme } from '@material-ui/core/styles/createMuiTheme'
import { isEmpty } from 'lodash'
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core'
import {
  Print as PrintIcon,
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@material-ui/icons'
import ReactToPrint, { PrintContextConsumer } from 'react-to-print'

import {
  Mode,
  ReceivingEvent,
  ReceivingHandler,
  ReceivingHandlerCallback,
} from '../../scanRouter/handlers'
import { PalletIdDialog } from 'src/modules/supply_chain/components/PalletIdDialog'
import { ASNUnit, Batch, Generic, Pallet } from 'src/api/models'
import { BulkLabels, PalletLabel } from 'src/components/PrintLabel'
import { TransloadPlacard } from 'src/components/PrintLayout'
import { CompactPath } from 'src/components/Tree'
import { GenericCard, ProductCard } from 'src/components/ModelCard'
import { DateField, MapSelectField, NumberField, TextField, useForm } from 'src/forms'
import { useAsyncPrint } from 'src/components/ToPrint/useAsyncPrint'

import { MeasurementCard } from './MeasurementCard'
import { SkuSelectionCard } from './SkuSelectionCard'

const useStyles = makeStyles((theme: Theme) => ({
  entry: {
    // display: 'block',
    height: 'initial',
    width: '100%',
    padding: theme.spacing(1),
    color: theme.palette.text.primary,
    transition: 'all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms',
    boxSizing: 'border-box',
    boxShadow: 'rgba(0, 0, 0, 0.12) 0 1px 6px, rgba(0, 0, 0, 0.12) 0 1px 4px',
    borderRadius: theme.shape.borderRadius,
  },
  label: {
    padding: theme.spacing(1),
    color: theme.palette.text.primary,
    background: 'rgba(0, 0, 0, 0.08)',
    boxSizing: 'border-box',
    boxShadow: 'rgba(0, 0, 0, 0.12) 0 1px 6px, rgba(0, 0, 0, 0.12) 0 1px 4px',
    borderRadius: theme.shape.borderRadius,
  },
  count: {
    padding: theme.spacing(1),
    color: theme.palette.text.primary,
    transition: 'all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms',
    boxSizing: 'border-box',
    boxShadow: 'rgba(0, 0, 0, 0.12) 0 1px 6px, rgba(0, 0, 0, 0.12) 0 1px 4px',
    borderRadius: theme.shape.borderRadius,
  },
  iconBtn: {
    padding: 2,
  },
  outlined: {
    display: 'inline-block',
    border: 'solid',
    borderWidth: '1px',
    borderRadius: '4px',
    padding: '6px',
  },
}))

interface CreatePalletDisplayProps {
  handler: ReceivingHandler
  onCancel?: () => void
}

const CreatePalletDisplay = (props: CreatePalletDisplayProps) => {
  const { handler, onCancel } = props
  const [batch, setBatch] = useState(false)
  const [saving, setSaving] = useState(false)
  const { Form } = useForm()

  useEffect(() => {
    const cb: ReceivingHandlerCallback = (data: any) => {
      if (data.event === ReceivingEvent.Save) {
        setSaving(data.saving)
      } else {
        onCancel?.()
      }
    }
    handler.registerCb(cb)
    return () => handler.removeCb(cb)
  }, [handler, onCancel, setSaving])

  return (
    <Form onSubmit={handler.createPallets}>
      <Grid container justify="center" spacing={4}>
        <Grid item xs={12} container spacing={2} alignItems="center">
          <Grid item>
            <NumberField name="count" label="Number of Pallets to Create" />
          </Grid>
          <Grid item xs={12}>
            <Divider style={{ marginTop: '1rem' }} />
          </Grid>
          <Grid item xs={12}>
            <Grid container alignItems="center" justify="center" spacing={4}>
              <Grid item>
                <Typography variant="h5">Add as a part of a Batch (optional)</Typography>
              </Grid>
              <Grid item>
                <IconButton onClick={() => setBatch(!batch)}>
                  {batch ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
          <Collapse in={batch}>
            <Grid item xs={12}>
              <MapSelectField
                name="batch_type"
                label="Type"
                options={{
                  [Batch.Type.batch]: 'Batch',
                  [Batch.Type.lot]: 'Lot',
                  [Batch.Type.po]: 'Purchase Order',
                }}
                defaultValue={Batch.Type.batch}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField name="client_uid" label="Number" />
            </Grid>
            <Grid item xs={12}>
              <DateField name="expires_on" label="Date Expires" />
            </Grid>
          </Collapse>
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={() => onCancel?.()}>
            Cancel
          </Button>
        </Grid>
        <Grid item>
          <Button type="submit" color="primary" variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Grid>
      </Grid>
    </Form>
  )
}

interface ReceivingDisplayProps {
  handler: ReceivingHandler
}

const ReceivingASNUnitsDisplay = React.memo((props: ReceivingDisplayProps) => {
  const { handler } = props
  const classes = useStyles()
  const [, refresh] = useState({})
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const cb: ReceivingHandlerCallback = (data) => {
      if (data.event === ReceivingEvent.Loaded) {
        refresh({})
      }
    }
    handler.registerCb(cb)
    return () => handler.removeCb(cb)
  }, [handler, refresh])

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Grid container spacing={4}>
          <Grid item>
            <Typography variant="h3">
              Received Units (
              {handler.asnUnits?.results
                .map((unit) => unit.numUnits)
                .reduce((sum: number, u: number) => sum + u, 0)}
              )
            </Typography>
          </Grid>
          {!!handler.asnUnits?.results?.length && (
            <Grid item>
              <IconButton onClick={() => setOpen(!open)}>
                {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Grid>
          )}
        </Grid>
      </Grid>
      {handler.loadingUnits ? (
        <Grid item container alignItems="center" xs={12}>
          <Grid item>
            <Box className={classes.entry}>Loading Units...</Box>
          </Grid>
        </Grid>
      ) : handler.asnUnits?.results?.length ? (
        <Collapse in={open}>
          {handler.asnUnits.results.map((item: ASNUnit) => (
            <Grid item container alignItems="center" key={item.sku?.sku} xs={12}>
              <Grid item xs={11}>
                <Box className={classes.entry}>{`[${item.sku?.sku}]${item.product?.name}`}</Box>
              </Grid>
              <Grid item xs={1} container alignItems="flex-end" justify="flex-end">
                <Box className={classes.count}>{`x${item.numUnits}`}</Box>
              </Grid>
            </Grid>
          ))}
        </Collapse>
      ) : (
        <Grid item container alignItems="center" xs={12}>
          <Grid item>
            <Box className={classes.entry}>None yet.</Box>
          </Grid>
        </Grid>
      )}
    </Grid>
  )
})

interface PalletDisplayProps {
  handler: ReceivingHandler
  pallet: Pallet
}

const PalletDisplay = (props: PalletDisplayProps) => {
  const { handler, pallet } = props
  const { print, renderPrintComponent } = useAsyncPrint()
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  return (
    <Card style={{ width: '100%' }}>
      {open && (
        <PalletIdDialog
          palletGuid={pallet.guid}
          open={open}
          handleClose={() => setOpen(false)}
          onSuccess={() => {
            handler.fetchPallets()
          }}
        />
      )}
      {renderPrintComponent()}
      <CardContent style={{ padding: '4px' }}>
        <Grid container spacing={4}>
          <Grid item xs={11}>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Grid container spacing={4}>
                  <Grid item>{pallet.name}</Grid>
                  <Grid item>({pallet.guid})</Grid>
                  <Grid item>Created {pallet.createdOn.format('lll')}</Grid>
                  {pallet.batch && <Grid item>[{pallet.batch?.name}]</Grid>}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={4}>
                  <Grid item>Client ID:</Grid>
                  <Grid item>{pallet.clientID}</Grid>
                  <Grid item>
                    <IconButton className={classes.iconBtn} onClick={() => setOpen(true)}>
                      <EditIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={1}>
            <IconButton
              className={classes.iconBtn}
              onClick={() => print((props) => <PalletLabel {...props} obj={pallet} />)}
            >
              <PrintIcon />
            </IconButton>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

const ReceivingASNPalletsDisplay = React.memo((props: ReceivingDisplayProps) => {
  const { handler } = props
  const printAllRef = useRef<HTMLDivElement>(null)
  const classes = useStyles()
  const [, refresh] = useState({})
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const cb: ReceivingHandlerCallback = (data) => {
      if (data.event === ReceivingEvent.Loaded) {
        refresh({})
      }
    }
    handler.registerCb(cb)
    return () => handler.removeCb(cb)
  }, [handler, refresh])

  return (
    <Grid container spacing={4}>
      <Grid item xs={10}>
        <Grid container spacing={4}>
          <Grid item>
            <Typography variant="h3">Received Pallets ({handler.asnPallets?.count})</Typography>
          </Grid>
          {!!handler.asnPallets?.results?.length && (
            <Grid item>
              <IconButton onClick={() => setOpen(!open)}>
                {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item xs={2}>
        {!!handler.asnPallets?.results?.length && (
          <ReactToPrint content={() => printAllRef.current}>
            <div style={{ display: 'none' }}>
              <div ref={printAllRef}>
                <BulkLabels items={handler.asnPallets?.results} />
              </div>
            </div>
            <PrintContextConsumer>
              {({ handlePrint }) => (
                <Button variant="contained" onClick={handlePrint} startIcon={<PrintIcon />}>
                  Print All
                </Button>
              )}
            </PrintContextConsumer>
          </ReactToPrint>
        )}
      </Grid>
      {handler.loadingPallets ? (
        <Grid item container alignItems="center" xs={12}>
          <Grid item>
            <Box className={classes.entry}>Loading Pallets...</Box>
          </Grid>
        </Grid>
      ) : handler.asnPallets?.results?.length ? (
        <Collapse in={open}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              {handler.asnPallets?.results.map((item: Pallet) => (
                <PalletDisplay handler={handler} pallet={item} key={item.guid} />
              ))}
            </Grid>
          </Grid>
        </Collapse>
      ) : (
        <Grid item container alignItems="center" xs={12}>
          <Grid item>
            <Box className={classes.entry}>None yet.</Box>
          </Grid>
        </Grid>
      )}
    </Grid>
  )
})

export const ReceivingDisplay = (props: ReceivingDisplayProps) => {
  const { handler } = props
  const classes = useStyles()
  const [saving, setSaving] = useState(false)
  const [addingPallets, setAddingPallets] = useState(false)
  const [error] = useState(false)
  const [, refresh] = useState({})
  const { print, renderPrintComponent } = useAsyncPrint()

  useEffect(() => {
    const cb: ReceivingHandlerCallback = (data: any) => {
      if (data.event === ReceivingEvent.Save) {
        setSaving(data.saving)
      } else {
        refresh({})
      }
    }
    handler.registerCb(cb)
    return () => handler.removeCb(cb)
  }, [handler, refresh, setSaving])

  if (addingPallets) {
    return <CreatePalletDisplay handler={handler} onCancel={() => setAddingPallets(false)} />
  }
  return (
    <Grid container justify="center" spacing={4}>
      {renderPrintComponent()}
      <Grid item>
        <GenericCard item={handler.asn} />
      </Grid>
      <Grid item container>
        <Grid item>Receiving To:</Grid>
        <Grid item>
          <GenericCard item={handler.to as Generic} />
        </Grid>
      </Grid>
      <Grid container item justify="center" spacing={6}>
        <Grid item>
          <Button
            color="primary"
            variant={handler.mode === Mode.Scan ? 'contained' : 'outlined'}
            onClick={() => {
              handler.setMode(Mode.Scan)
            }}
          >
            Scan
          </Button>
        </Grid>
        <Grid item>
          <Button
            color="primary"
            variant={handler.mode === Mode.Unscan ? 'contained' : 'outlined'}
            onClick={() => {
              handler.setMode(Mode.Unscan)
            }}
          >
            UnScan
          </Button>
        </Grid>
      </Grid>
      {!!handler.asn.object.transloadNumber && (
        <Grid item xs={12} container spacing={2}>
          <Grid item xs={8}>
            <Typography variant="h3">Transload Number</Typography>
          </Grid>
          <Grid item xs={4}>
            <IconButton
              disabled={!handler.asnPallets?.count}
              onClick={() => {
                print((props) => <TransloadPlacard {...props} asn={handler.asn.object} />)
              }}
            >
              <PrintIcon />
            </IconButton>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h5">{handler.asn.object.transloadNumber}</Typography>
          </Grid>
        </Grid>
      )}
      <Grid item xs={12}>
        <Button variant="contained" onClick={() => setAddingPallets(true)}>
          Add Pallets &gt;
        </Button>
      </Grid>
      {!isEmpty(handler.receiving) && (
        <Grid item xs={12} container spacing={2}>
          <Grid item>
            <Typography variant="h3">Active Changes</Typography>
          </Grid>
          <Grid item>
            {handler.receiving.map(([location, items]) => {
              if (handler.toMeasure) {
                return (
                  <MeasurementCard
                    key={location.object.guid}
                    product={handler.toMeasure.product.object}
                    unitOfMeasure={handler.toMeasure.sku?.object.unitOfMeasureDisplay}
                    measure={handler.provideMeasure(items, handler.toMeasure)}
                  />
                )
              }
              return (
                <React.Fragment key={location.object.guid}>
                  <CompactPath title="Receiving to" location={location.object} />
                  {items.map((item) => {
                    return (
                      <Grid key={item.key} item container spacing={2} alignItems="center">
                        <Grid item xs={10}>
                          <>
                            {!item.sku && item.skus?.length ? (
                              <SkuSelectionCard
                                skus={item.skus}
                                select={handler.skuSelector(items, item)}
                              />
                            ) : (
                              <ProductCard
                                item={item.product.object}
                                sku={item.sku?.object}
                                selectedCase={item.productCase}
                                selectCase={handler.caseSelector(items, item)}
                              />
                            )}
                          </>
                        </Grid>
                        <Grid item xs={2}>
                          {!!item.count && (
                            <span className={classes.outlined}>
                              x{`${item.count} ${item.sku?.object.unitOfMeasureDisplay}`}
                            </span>
                          )}
                        </Grid>
                      </Grid>
                    )
                  })}
                </React.Fragment>
              )
            })}
          </Grid>
        </Grid>
      )}
      <Grid item xs={12} spacing={2} alignItems="center">
        <ReceivingASNUnitsDisplay handler={handler} />
      </Grid>
      <Grid item xs={12} spacing={2} alignItems="center">
        <ReceivingASNPalletsDisplay handler={handler} />
      </Grid>
      <Grid item>
        <Button
          color="primary"
          variant="contained"
          disabled={!!handler.toResolve || saving}
          onClick={handler.save}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Grid>
      {error && (
        <>
          <Grid item>
            <Typography color="error">Failed move: {error}</Typography>
          </Grid>
          <Grid item>
            <Typography>Scan to restart scan sequence</Typography>
          </Grid>
        </>
      )}
    </Grid>
  )
}
